import React, { useEffect, useState, useCallback } from 'react';
import apiClient, { formatApiError } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import * as PhosphorIcons from '@phosphor-icons/react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Plus, Trash, Pencil, Export, UploadSimple, DotsSixVertical } = PhosphorIcons;

const SortableTrackItem = ({ track, index, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: track.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2 rounded-md bg-[#f5f0e8] border border-[#c5d4c8] ${isDragging ? 'border-blue-600 shadow-lg' : ''}`}
      data-testid={`sortable-track-${track.id}`}
    >
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-[#8a8477] hover:text-[#6b665c]" data-testid={`drag-handle-${track.id}`}>
        <DotsSixVertical size={16} />
      </button>
      <span className="text-xs text-[#8a8477] font-mono w-6">{index + 1}</span>
      <span className="text-sm flex-1 truncate">{track.title} — {track.artist}</span>
      <button onClick={() => onRemove(track.id)} className="text-[#8a8477] hover:text-red-400 transition-colors" data-testid={`remove-track-${track.id}`}>
        <Trash size={14} />
      </button>
    </div>
  );
};

const Playlists = () => {
  const [playlists, setPlaylists] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const importRef = React.useRef(null);
  const [formData, setFormData] = useState({ name: '', description: '', track_ids: [], transition: 'cut', fade_duration_ms: 2500 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const fetchData = useCallback(async () => {
    try {
      const [playlistsRes, tracksRes] = await Promise.all([
        apiClient.get('/playlists'),
        apiClient.get('/tracks')
      ]);
      setPlaylists(playlistsRes.data);
      setTracks(tracksRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/playlists', formData);
      toast.success('Playlist created successfully');
      setOpen(false);
      setFormData({ name: '', description: '', track_ids: [], transition: 'cut', fade_duration_ms: 2500 });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.put(`/playlists/${selectedPlaylist.id}`, formData);
      toast.success('Playlist updated successfully');
      setEditOpen(false);
      setSelectedPlaylist(null);
      setFormData({ name: '', description: '', track_ids: [], transition: 'cut', fade_duration_ms: 2500 });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) return;
    try {
      await apiClient.delete(`/playlists/${id}`);
      toast.success('Playlist deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openEditDialog = (playlist) => {
    setSelectedPlaylist(playlist);
    setFormData({
      name: playlist.name,
      description: playlist.description || '',
      track_ids: playlist.track_ids || [],
      transition: playlist.transition || 'cut',
      fade_duration_ms: playlist.fade_duration_ms || 2500,
    });
    setEditOpen(true);
  };

  const toggleTrack = (trackId) => {
    setFormData(prev => {
      const ids = prev.track_ids.includes(trackId)
        ? prev.track_ids.filter(id => id !== trackId)
        : [...prev.track_ids, trackId];
      return { ...prev, track_ids: ids };
    });
  };

  const removeTrackFromPlaylist = (trackId) => {
    setFormData(prev => ({ ...prev, track_ids: prev.track_ids.filter(id => id !== trackId) }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFormData(prev => {
      const oldIndex = prev.track_ids.indexOf(active.id);
      const newIndex = prev.track_ids.indexOf(over.id);
      return { ...prev, track_ids: arrayMove(prev.track_ids, oldIndex, newIndex) };
    });
  };

  const selectedTracks = formData.track_ids.map(id => tracks.find(t => t.id === id)).filter(Boolean);

  // ── Transition picker (shared by Create + Edit dialogs) ──────────────────
  // Playlist-level control for what happens when one track ends and the next
  // starts. "cut" is the default (unchanged behaviour). "fade" enables the
  // kiosk's two-audio-element crossfade path with a configurable duration.
  const TransitionPicker = () => (
    <div className="space-y-2">
      <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Track transition</Label>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setFormData(p => ({ ...p, transition: 'cut' }))}
          className={`p-3 rounded-md border text-left transition-all ${
            formData.transition === 'cut'
              ? 'bg-[#e4ebe5] border-[#7c9082] ring-1 ring-[#7c9082]'
              : 'bg-[#f5f0e8] border-[#c5d4c8] hover:bg-[#ede8dc]'
          }`}
          data-testid="transition-cut-option"
        >
          <p className="text-sm font-semibold text-[#2d2a26]">Cut</p>
          <p className="text-xs text-[#6b665c] mt-0.5">Next track starts instantly</p>
        </button>
        <button
          type="button"
          onClick={() => setFormData(p => ({ ...p, transition: 'fade' }))}
          className={`p-3 rounded-md border text-left transition-all ${
            formData.transition === 'fade'
              ? 'bg-[#e4ebe5] border-[#7c9082] ring-1 ring-[#7c9082]'
              : 'bg-[#f5f0e8] border-[#c5d4c8] hover:bg-[#ede8dc]'
          }`}
          data-testid="transition-fade-option"
        >
          <p className="text-sm font-semibold text-[#2d2a26]">Crossfade</p>
          <p className="text-xs text-[#6b665c] mt-0.5">Fades out ↔ fades in</p>
        </button>
      </div>
      {formData.transition === 'fade' && (
        <div className="pt-2" data-testid="fade-duration-row">
          <div className="flex items-center justify-between mb-1">
            <Label className="font-mono text-[10px] uppercase tracking-wider text-[#6b665c]">
              Fade duration
            </Label>
            <span className="text-xs text-[#2d2a26] font-mono">
              {(formData.fade_duration_ms / 1000).toFixed(1)}s
            </span>
          </div>
          <input
            type="range"
            min="500"
            max="8000"
            step="500"
            value={formData.fade_duration_ms}
            onChange={(e) => setFormData(p => ({ ...p, fade_duration_ms: parseInt(e.target.value, 10) }))}
            className="w-full accent-[#7c9082]"
            data-testid="fade-duration-slider"
          />
          <p className="text-[10px] text-[#8a8477] mt-1 font-mono">
            0.5s to 8.0s. The current track starts fading out this long before it ends; the next fades in over the same span.
          </p>
        </div>
      )}
    </div>
  );

  const handleExport = async (playlistId) => {
    try {
      const res = await apiClient.get(`/playlists/${playlistId}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `playlist_${res.data.playlist.name.replace(/\s+/g, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Playlist exported');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await apiClient.post('/playlists/import', data);
      toast.success(`Imported "${res.data.playlist.name}" with ${res.data.matched_tracks} tracks`);
      if (res.data.unmatched_tracks.length > 0) {
        toast.info(`${res.data.unmatched_tracks.length} tracks not found in your library`);
      }
      fetchData();
    } catch (error) {
      toast.error('Invalid playlist file');
    }
    if (importRef.current) importRef.current.value = '';
  };

  const TrackSelector = () => (
    <div>
      <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Add Tracks</Label>
      <div className="space-y-2 max-h-40 overflow-y-auto bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3">
        {tracks.filter(t => !formData.track_ids.includes(t.id)).map((track) => (
          <div key={track.id} className="flex items-center gap-2">
            <Checkbox
              id={`add-track-${track.id}`}
              checked={false}
              onCheckedChange={() => toggleTrack(track.id)}
              data-testid={`track-checkbox-${track.id}`}
            />
            <label htmlFor={`add-track-${track.id}`} className="text-sm cursor-pointer">{track.title} - {track.artist}</label>
          </div>
        ))}
        {tracks.filter(t => !formData.track_ids.includes(t.id)).length === 0 && (
          <p className="text-sm text-[#8a8477]">All tracks added to playlist</p>
        )}
      </div>
    </div>
  );

  const TrackOrderList = () => (
    <div>
      <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">
        Track Order (drag to reorder)
      </Label>
      {selectedTracks.length === 0 ? (
        <p className="text-sm text-[#8a8477] bg-[#f5f0e8] border border-[#c5d4c8] rounded-md p-3">No tracks selected</p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={formData.track_ids} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="track-order-list">
              {selectedTracks.map((track, idx) => (
                <SortableTrackItem key={track.id} track={track} index={idx} onRemove={removeTrackFromPlaylist} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="playlists-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="playlists-title">Playlists</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">Create and manage playlists</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => importRef.current?.click()} className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8]" data-testid="import-playlist-button">
            <UploadSimple size={18} className="mr-2" />Import
          </Button>
          <input ref={importRef} type="file" accept=".json" onChange={handleImportFile} className="hidden" />

          {/* Create Dialog */}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="add-playlist-button">
                <Plus size={20} className="mr-2" />Create Playlist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-mono">Create New Playlist</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-playlist-form">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Playlist Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required data-testid="playlist-name-input" />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" rows={2} data-testid="playlist-description-input" />
                </div>
                <TrackSelector />
                <TrackOrderList />
                <TransitionPicker />
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="submit-playlist-button">Create Playlist</Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Dialog */}
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-mono">Edit Playlist</DialogTitle></DialogHeader>
              <form onSubmit={handleEdit} className="space-y-4" data-testid="edit-playlist-form">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Playlist Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" required />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Description</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" rows={2} />
                </div>
                <TrackSelector />
                <TrackOrderList />
                <TransitionPicker />
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="submit-edit-playlist-button">Update Playlist</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {playlists.map((playlist) => (
          <div key={playlist.id} className="bg-white border border-[#c5d4c8] rounded-md p-6 transition-all duration-200 hover:-translate-y-1 hover:border-[#c5d4c8]" data-testid={`playlist-card-${playlist.id}`}>
            <div className="mb-4">
              <h3 className="text-lg font-bold mb-1">{playlist.name}</h3>
              {playlist.description && <p className="text-sm text-[#6b665c] mb-2">{playlist.description}</p>}
              <p className="text-xs text-[#8a8477] font-mono">{playlist.track_ids?.length || 0} tracks</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => openEditDialog(playlist)} className="flex-1 bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8] text-sm" data-testid={`edit-playlist-${playlist.id}`}>
                <Pencil size={16} className="mr-2" />Edit
              </Button>
              <Button onClick={() => handleExport(playlist.id)} className="bg-[#e4ebe5] hover:bg-[#d9d3c5] text-[#5a6e5f] border border-[#c5d4c8]" data-testid={`export-playlist-${playlist.id}`}>
                <Export size={16} />
              </Button>
              <Button onClick={() => handleDelete(playlist.id)} className="bg-[#fce8eb] hover:bg-[#f0c4cb] text-[#b5636e] border-0" data-testid={`delete-playlist-${playlist.id}`}>
                <Trash size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {playlists.length === 0 && (
        <div className="text-center py-12 bg-white border border-[#c5d4c8] rounded-md">
          <p className="text-[#8a8477] font-mono">No playlists yet. Create your first playlist.</p>
        </div>
      )}
    </div>
  );
};

export default Playlists;
